/**
 * Vorbereitung: Was muss heute Abend passieren, damit morgen läuft?
 *
 * Zwei Fragen, eine Antwort:
 *
 * 1. **Was ist vorzubereiten?** Gerichte des nächsten Tages mit einem Hinweis
 *    für den Vorabend („Hafer einweichen").
 * 2. **Was fehlt noch zuhause?** Der Bedarf des nächsten Tages minus Vorrat —
 *    und zwar **ohne** die Einkaufsliste abzuziehen. Was abends noch auf der
 *    Liste steht, ist nicht gekauft; es fehlt morgen früh genauso.
 *
 * Diese Datei ist die eine Stelle, an der das gerechnet wird. Die Kachel in der
 * App und die Benachrichtigung des Schedulers benutzen dieselbe Funktion — sonst
 * meldete das Telefon etwas anderes, als die App zeigt.
 */

import { buildShoppingNeeds } from './shopping.js';
import type { NeedLine, NeedsInput } from './shopping.js';
import { DAY_END_WALL_TIME } from './reminders.js';
import type { DaySlot } from './nutrition.js';
import { addDays, isWallTime, wallTimeToUtc } from './time.js';
import type { IsoDate, WallTime } from './time.js';

/**
 * Wie lange vor der eingestellten Zeit der Scheduler die Erinnerung anlegt.
 *
 * Spät mit Absicht: Ihr Text wird beim Anlegen festgeschrieben. Wer um 18 Uhr
 * noch den Plan für morgen ändert, soll um 20 Uhr nicht die alte Fassung lesen.
 */
export const PREP_PLAN_LEAD_MIN = 15;

/**
 * Späteste erlaubte Zeit. Nach `DAY_END_WALL_TIME` (21:30) laufen offene
 * Erinnerungen ab — eine um 22:00 fällige würde also nie gesendet, sondern
 * gleich verworfen. 21:00 lässt Platz für die erste Nachfass-Stufe.
 */
export const PREP_LATEST_TIME: WallTime = '21:00';

export function isValidPrepTime(value: string): boolean {
  return isWallTime(value) && value <= PREP_LATEST_TIME && value < DAY_END_WALL_TIME;
}

export interface PrepPlanEntry {
  slot: DaySlot;
  recipeId: string | null;
}

export interface PrepRecipe {
  id: string;
  name: string;
  prepNote: string | null;
}

export interface PrepTask {
  recipeId: string;
  recipeName: string;
  slot: DaySlot;
  note: string;
}

export interface PrepSummary {
  tasks: PrepTask[];
  /** Fehlt zuhause: zu kaufen oder zu prüfen. */
  missing: NeedLine[];
  /** Gerichte ohne Zutatenliste — ob etwas fehlt, kann die App nicht sagen. */
  unknownIngredients: string[];
}

/**
 * Die Zusammenfassung für einen Tag.
 *
 * `needs` bekommt den Plan **dieses einen Tages** und eine leere Liste
 * (`onList: []`) — siehe Kopfkommentar.
 */
export function prepForDay(input: {
  plan: readonly PrepPlanEntry[];
  recipes: readonly PrepRecipe[];
  needs: Omit<NeedsInput, 'onList'>;
}): PrepSummary {
  const recipes = new Map(input.recipes.map((recipe) => [recipe.id, recipe]));
  const seen = new Set<string>();
  const tasks: PrepTask[] = [];

  for (const entry of input.plan) {
    if (!entry.recipeId || seen.has(entry.recipeId)) continue;
    const recipe = recipes.get(entry.recipeId);
    const note = recipe?.prepNote?.trim();
    if (!recipe || !note) continue;
    seen.add(recipe.id);
    tasks.push({ recipeId: recipe.id, recipeName: recipe.name, slot: entry.slot, note });
  }

  const needs = buildShoppingNeeds({ ...input.needs, onList: [] });

  return {
    tasks,
    missing: needs.lines.filter((line) => line.status === 'buy' || line.status === 'check'),
    unknownIngredients: needs.unstructured.map((recipe) => recipe.name),
  };
}

export function hasAnythingToSay(summary: PrepSummary): boolean {
  return summary.tasks.length > 0 || summary.missing.length > 0;
}

/**
 * Wann die Erinnerung für `forDate` fällig ist: am Vorabend zur eingestellten Zeit.
 */
export function prepDueAt(forDate: IsoDate, time: WallTime, timeZone: string): Date {
  return wallTimeToUtc(addDays(forDate, -1), time, timeZone);
}

/**
 * Text der Benachrichtigung — kurz, weil iOS nach zwei Zeilen abschneidet.
 *
 * Deutsch wie alle Texte des Schedulers. Gemeldet wird nur, was es gibt; eine
 * leere Zusammenfassung erzeugt gar keine Erinnerung (siehe `hasAnythingToSay`).
 */
export function prepReminderText(summary: PrepSummary): { title: string; body: string } {
  const parts: string[] = [];
  if (summary.tasks.length > 0) {
    parts.push(summary.tasks.map((task) => task.note).join(' · '));
  }
  if (summary.missing.length > 0) {
    const names = summary.missing.map((line) =>
      line.toBuyG !== null && line.toBuyG > 0 ? `${line.name} ${line.toBuyG} g` : line.name,
    );
    parts.push(`Fehlt: ${names.join(', ')}`);
  }
  return { title: 'Für morgen vorbereiten', body: parts.join('\n') };
}
